import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getProjectDashboard, createProjectTask, updateProjectTask, deleteProjectTask, createTimeEntry, deleteTimeEntry, updateMilestones, getProjectComments, createProjectComment, deleteProjectComment, getProjectFiles, uploadProjectFile, deleteProjectFile, getProjectActivity, getProjectFinance } from '../lib/api';
import ProjectPlanningTab from './ProjectPlanningTab';
import ProjectReportsTab from './ProjectReportsTab';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowLeft, Plus, Clock, DollarSign, CheckCircle, TrendingUp, Loader2, Trash2, Target, FileText, Receipt, Flag, MessageSquare, Paperclip, Upload, Download, Activity, AlertTriangle, Send, CalendarDays, FileBarChart } from 'lucide-react';
import { toast } from 'sonner';

const priorityConfig = { high: { label: 'Haute', color: 'bg-red-100 text-red-700' }, medium: { label: 'Moyenne', color: 'bg-amber-100 text-amber-700' }, low: { label: 'Basse', color: 'bg-slate-100 text-slate-500' } };
const taskStatusConfig = { todo: { label: 'A faire', color: 'bg-slate-100 text-slate-600' }, in_progress: { label: 'En cours', color: 'bg-blue-100 text-blue-700' }, done: { label: 'Fait', color: 'bg-emerald-100 text-emerald-700' } };
const PIE_COLORS = ['#ef4444', '#f59e0b', '#3b82f6', '#10b981', '#8b5cf6', '#ec4899', '#06b6d4'];

const ProjectDetailPage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [timeDialogOpen, setTimeDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [taskForm, setTaskForm] = useState({ title: '', description: '', priority: 'medium', assignee: '', deadline: '', start_date: '', estimated_hours: 0, dependencies: [] });
  const [timeForm, setTimeForm] = useState({ hours: 1, description: '', date: new Date().toISOString().slice(0, 10), task_id: '' });
  const [milestones, setMilestones] = useState([]);
  const [saving, setSaving] = useState(false);
  // Collaboration
  const [comments, setComments] = useState([]);
  const [commentText, setCommentText] = useState('');
  const [files, setFiles] = useState([]);
  const [activities, setActivities] = useState([]);
  const [finance, setFinance] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => { loadDashboard(); }, [projectId]);

  const loadDashboard = async () => {
    try {
      const res = await getProjectDashboard(projectId);
      setData(res.data);
      setMilestones(res.data.project.milestones || []);
    } catch (err) { toast.error('Erreur chargement'); } finally { setLoading(false); }
  };

  const loadComments = async () => {
    try { const r = await getProjectComments(projectId); setComments(r.data); } catch (e) { console.error(e); }
  };
  const loadFiles = async () => {
    try { const r = await getProjectFiles(projectId); setFiles(r.data); } catch (e) { console.error(e); }
  };
  const loadActivity = async () => {
    try { const r = await getProjectActivity(projectId); setActivities(r.data); } catch (e) { console.error(e); }
  };
  const loadFinance = async () => {
    try { const r = await getProjectFinance(projectId); setFinance(r.data); } catch (e) { console.error(e); }
  };

  useEffect(() => {
    if (tab === 'collab') { loadComments(); loadFiles(); }
    if (tab === 'activity') loadActivity();
    if (tab === 'finance') loadFinance();
  }, [tab]);

  const handleCreateTask = async (e) => {
    e.preventDefault();
    if (!taskForm.title) return;
    setSaving(true);
    try {
      if (editingTask) await updateProjectTask(projectId, editingTask.id, taskForm);
      else await createProjectTask(projectId, taskForm);
      setTaskDialogOpen(false); setEditingTask(null);
      setTaskForm({ title: '', description: '', priority: 'medium', assignee: '', deadline: '', start_date: '', estimated_hours: 0, dependencies: [] });
      loadDashboard(); toast.success(editingTask ? 'Mise a jour' : 'Tache creee');
    } catch (err) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const handleTaskStatusChange = async (task, newStatus) => {
    try { await updateProjectTask(projectId, task.id, { status: newStatus }); loadDashboard(); } catch (err) { toast.error('Erreur'); }
  };

  const handleDeleteTask = async (taskId) => {
    try { await deleteProjectTask(projectId, taskId); loadDashboard(); } catch (err) { toast.error('Erreur'); }
  };

  const handleCreateTime = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      await createTimeEntry(projectId, timeForm);
      setTimeDialogOpen(false); setTimeForm({ hours: 1, description: '', date: new Date().toISOString().slice(0, 10) });
      loadDashboard(); toast.success('Temps enregistre');
    } catch (err) { toast.error('Erreur'); } finally { setSaving(false); }
  };

  const handleDeleteTime = async (entryId) => {
    try { await deleteTimeEntry(projectId, entryId); loadDashboard(); } catch (err) { toast.error('Erreur'); }
  };

  const handleSaveMilestones = async () => {
    try { await updateMilestones(projectId, milestones); toast.success('Jalons enregistres'); } catch (e) { toast.error('Erreur'); }
  };

  const handleSendComment = async () => {
    if (!commentText.trim()) return;
    try { await createProjectComment(projectId, { text: commentText }); setCommentText(''); loadComments(); } catch (e) { toast.error('Erreur'); }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try { await uploadProjectFile(projectId, file); loadFiles(); toast.success('Fichier ajoute'); } catch (e) { toast.error('Erreur upload'); }
    e.target.value = '';
  };

  const handleDeleteFile = async (fileId) => {
    try { await deleteProjectFile(projectId, fileId); loadFiles(); toast.success('Supprime'); } catch (e) { toast.error('Erreur'); }
  };

  const fmt = (n) => new Intl.NumberFormat('fr-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) + ' CHF';
  const fmtSize = (bytes) => bytes > 1024 * 1024 ? (bytes / 1024 / 1024).toFixed(1) + ' MB' : (bytes / 1024).toFixed(0) + ' KB';

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div>;
  if (!data) return <div className="text-center py-12 text-slate-500">Projet non trouve</div>;

  const { project, budget, total_expenses, total_invoiced, total_hours, time_cost, total_cost, budget_remaining, budget_percent, profit, tasks_total, tasks_done, tasks_in_progress, progress, tasks, recent_time, client_name } = data;

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: Target },
    { id: 'tasks', label: `Taches (${tasks_total})`, icon: CheckCircle },
    { id: 'time', label: `Temps (${total_hours}h)`, icon: Clock },
    { id: 'planning', label: 'Planification', icon: CalendarDays },
    { id: 'collab', label: 'Collaboration', icon: MessageSquare },
    { id: 'milestones', label: 'Jalons', icon: Flag },
    { id: 'finance', label: 'Finances', icon: DollarSign },
    { id: 'reports', label: 'Rapports', icon: FileBarChart },
    { id: 'activity', label: 'Activite', icon: Activity },
  ];

  return (
    <div className="space-y-6" data-testid="project-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate('/app/projects')}><ArrowLeft className="w-4 h-4" /></Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="w-4 h-4 rounded-full" style={{ backgroundColor: project.color }} />
            <h1 className="text-2xl font-bold text-slate-900">{project.name}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${project.status === 'active' ? 'bg-emerald-100 text-emerald-700' : project.status === 'completed' ? 'bg-blue-100 text-blue-700' : project.status === 'paused' ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}>
              {project.status === 'active' ? 'En cours' : project.status === 'completed' ? 'Termine' : project.status === 'paused' ? 'En pause' : 'Annule'}
            </span>
          </div>
          {client_name && <p className="text-sm text-slate-500 mt-0.5">Client: {client_name}</p>}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b border-slate-200 overflow-x-auto">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)} className={`px-3 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px whitespace-nowrap flex items-center gap-1.5 ${tab === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`tab-${t.id}`}>
              <Icon className="w-3.5 h-3.5" />{t.label}
            </button>
          );
        })}
      </div>

      {/* ===== OVERVIEW TAB ===== */}
      {tab === 'overview' && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {[
              { label: 'Budget', value: fmt(budget), color: '' },
              { label: 'Couts', value: fmt(total_cost), color: 'text-red-600' },
              { label: 'Facture', value: fmt(total_invoiced), color: 'text-emerald-600' },
              { label: 'Profit', value: fmt(profit), color: profit >= 0 ? 'text-green-600' : 'text-red-600' },
              { label: 'Heures', value: `${total_hours}h`, color: '' },
              { label: 'Avancement', value: `${progress}%`, color: '' },
            ].map((kpi, i) => (
              <Card key={i} className="border-slate-200"><CardContent className="p-4">
                <p className="text-xs text-slate-500 mb-1">{kpi.label}</p>
                <p className={`text-xl font-bold ${kpi.color}`}>{kpi.value}</p>
              </CardContent></Card>
            ))}
          </div>
          {budget > 0 && (
            <Card className="border-slate-200"><CardContent className="p-5">
              <div className="flex justify-between mb-2">
                <span className="text-sm font-medium">Budget utilise</span>
                <span className="text-sm text-slate-500">{fmt(total_cost)} / {fmt(budget)} ({budget_percent.toFixed(0)}%)</span>
              </div>
              <Progress value={Math.min(100, budget_percent)} className="h-2" />
              {budget_percent > 90 && <p className="text-xs text-red-600 mt-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" />Attention: budget presque epuise</p>}
            </CardContent></Card>
          )}
          <Card className="border-slate-200"><CardContent className="p-5">
            <div className="flex justify-between mb-2"><span className="text-sm font-medium">Taches</span><span className="text-sm text-slate-500">{tasks_done}/{tasks_total}</span></div>
            <Progress value={progress} className="h-2" />
          </CardContent></Card>
        </div>
      )}

      {/* ===== TASKS TAB ===== */}
      {tab === 'tasks' && (
        <div className="space-y-4">
          <div className="flex justify-end"><Button onClick={() => { setEditingTask(null); setTaskForm({ title: '', description: '', priority: 'medium', assignee: '', deadline: '', start_date: '', estimated_hours: 0, dependencies: [] }); setTaskDialogOpen(true); }} className="bg-blue-600 hover:bg-blue-700" data-testid="add-task-btn"><Plus className="w-4 h-4 mr-2" />Tache</Button></div>
          {['todo', 'in_progress', 'done'].map(status => {
            const sc = taskStatusConfig[status];
            const sts = tasks.filter(t => t.status === status);
            return (
              <div key={status}>
                <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${status === 'todo' ? 'bg-slate-400' : status === 'in_progress' ? 'bg-blue-500' : 'bg-emerald-500'}`} />{sc.label} ({sts.length})
                </h3>
                <div className="space-y-2">
                  {sts.map(task => (
                    <Card key={task.id} className="border-slate-200">
                      <CardContent className="p-3 flex items-center gap-3">
                        <button onClick={() => handleTaskStatusChange(task, status === 'todo' ? 'in_progress' : status === 'in_progress' ? 'done' : 'todo')} className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${task.status === 'done' ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 hover:border-blue-400'}`}>
                          {task.status === 'done' && <CheckCircle className="w-3 h-3 text-white" />}
                        </button>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${task.status === 'done' ? 'line-through text-slate-400' : 'text-slate-900'}`}>{task.title}</p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${priorityConfig[task.priority]?.color}`}>{priorityConfig[task.priority]?.label}</span>
                            {task.assignee && <span className="text-[10px] text-slate-400">{task.assignee}</span>}
                            {task.deadline && <span className="text-[10px] text-slate-400">{task.deadline}</span>}
                          </div>
                        </div>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => { setEditingTask(task); setTaskForm({ title: task.title, description: task.description || '', priority: task.priority, assignee: task.assignee || '', deadline: task.deadline || '', start_date: task.start_date || '', estimated_hours: task.estimated_hours || 0, dependencies: task.dependencies || [] }); setTaskDialogOpen(true); }}><Flag className="w-3 h-3" /></Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTask(task.id)}><Trash2 className="w-3 h-3" /></Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== TIME TAB ===== */}
      {tab === 'time' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-slate-500">Total: <strong className="text-slate-900">{total_hours}h</strong> = <strong className="text-slate-900">{fmt(time_cost)}</strong></div>
            <Button onClick={() => setTimeDialogOpen(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="add-time-btn"><Plus className="w-4 h-4 mr-2" />Temps</Button>
          </div>
          {recent_time.length > 0 ? (
            <Card className="border-slate-200"><CardContent className="p-0">
              <table className="w-full"><thead><tr className="border-b bg-slate-50 text-xs text-slate-500 uppercase"><th className="text-left p-3">Date</th><th className="text-left p-3">Description</th><th className="text-right p-3">Heures</th><th className="text-right p-3">Cout</th><th className="p-3"></th></tr></thead>
              <tbody>{recent_time.map(e => (
                <tr key={e.id} className="border-b border-slate-100 hover:bg-slate-50"><td className="p-3 text-sm">{e.date}</td><td className="p-3 text-sm text-slate-600">{e.description || '-'}</td><td className="p-3 text-sm text-right font-medium">{e.hours}h</td><td className="p-3 text-sm text-right">{fmt(e.cost)}</td><td className="p-3 text-right"><Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500" onClick={() => handleDeleteTime(e.id)}><Trash2 className="w-3 h-3" /></Button></td></tr>
              ))}</tbody></table>
            </CardContent></Card>
          ) : <Card className="border-slate-200"><CardContent className="py-8 text-center text-slate-500">Aucune entree de temps</CardContent></Card>}
        </div>
      )}

      {/* ===== PLANNING TAB ===== */}
      {tab === 'planning' && <ProjectPlanningTab projectId={projectId} projectColor={project.color} />}

      {/* ===== COLLABORATION TAB ===== */}
      {tab === 'collab' && (
        <div className="space-y-6">
          {/* Comments */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><MessageSquare className="w-4 h-4" />Commentaires ({comments.length})</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 mb-4">
                <Input value={commentText} onChange={(e) => setCommentText(e.target.value)} placeholder="Ecrire un commentaire..." onKeyDown={(e) => e.key === 'Enter' && handleSendComment()} data-testid="comment-input" className="flex-1" />
                <Button onClick={handleSendComment} disabled={!commentText.trim()} className="bg-blue-600 hover:bg-blue-700" data-testid="send-comment-btn"><Send className="w-4 h-4" /></Button>
              </div>
              <div className="space-y-3 max-h-[400px] overflow-y-auto">
                {comments.map(c => (
                  <div key={c.id} className="flex gap-3 p-3 bg-slate-50 rounded-lg">
                    <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-xs font-bold text-blue-700">{(c.user_name || c.user_email || '?')[0].toUpperCase()}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-slate-900">{c.user_name || c.user_email}</span>
                        <span className="text-xs text-slate-400">{new Date(c.created_at).toLocaleString('fr-CH')}</span>
                      </div>
                      <p className="text-sm text-slate-600 mt-0.5">{c.text}</p>
                    </div>
                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-slate-400 hover:text-red-500" onClick={() => { deleteProjectComment(projectId, c.id).then(loadComments); }}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
                {comments.length === 0 && <p className="text-center text-slate-400 py-4">Aucun commentaire</p>}
              </div>
            </CardContent>
          </Card>

          {/* Files */}
          <Card className="border-slate-200">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><Paperclip className="w-4 h-4" />Fichiers ({files.length})</CardTitle>
                <div>
                  <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                  <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} data-testid="upload-file-btn"><Upload className="w-3 h-3 mr-1" />Ajouter</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {files.map(f => (
                  <div key={f.id} className="flex items-center gap-3 p-2.5 border border-slate-100 rounded-lg hover:bg-slate-50">
                    <Paperclip className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900 truncate">{f.filename}</p>
                      <p className="text-xs text-slate-400">{fmtSize(f.size)} - {f.user_name} - {new Date(f.created_at).toLocaleDateString('fr-CH')}</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/projects/${projectId}/files/${f.id}/download?auth=${localStorage.getItem('token')}`, '_blank')}><Download className="w-3.5 h-3.5" /></Button>
                    <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteFile(f.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
                  </div>
                ))}
                {files.length === 0 && <p className="text-center text-slate-400 py-4">Aucun fichier</p>}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ===== MILESTONES TAB ===== */}
      {tab === 'milestones' && (
        <div className="space-y-4">
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setMilestones([...milestones, { id: Date.now().toString(), title: '', date: '', completed: false }])}><Plus className="w-4 h-4 mr-2" />Jalon</Button>
            <Button onClick={handleSaveMilestones} className="bg-blue-600 hover:bg-blue-700">Enregistrer</Button>
          </div>
          {milestones.length > 0 ? (
            <div className="space-y-3">
              {milestones.map((m, i) => (
                <Card key={m.id || i} className="border-slate-200"><CardContent className="p-3 flex items-center gap-3">
                  <button onClick={() => { const u = [...milestones]; u[i] = { ...m, completed: !m.completed }; setMilestones(u); }} className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${m.completed ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300'}`}>{m.completed && <CheckCircle className="w-4 h-4 text-white" />}</button>
                  <Input value={m.title} onChange={(e) => { const u = [...milestones]; u[i] = { ...m, title: e.target.value }; setMilestones(u); }} placeholder="Nom du jalon" className="flex-1" />
                  <Input type="date" value={m.date || ''} onChange={(e) => { const u = [...milestones]; u[i] = { ...m, date: e.target.value }; setMilestones(u); }} className="w-40" />
                  <Button variant="ghost" size="sm" className="text-red-500" onClick={() => setMilestones(milestones.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></Button>
                </CardContent></Card>
              ))}
            </div>
          ) : <Card className="border-slate-200"><CardContent className="py-8 text-center text-slate-500"><Target className="w-8 h-8 mx-auto mb-2 text-slate-300" />Aucun jalon</CardContent></Card>}
        </div>
      )}

      {/* ===== FINANCE TAB ===== */}
      {tab === 'finance' && finance && (
        <div className="space-y-6">
          {/* Budget alert */}
          {finance.budget_alert && (
            <div className={`p-4 rounded-lg flex items-center gap-2 ${finance.budget_alert === 'critical' ? 'bg-red-50 border border-red-200 text-red-800' : finance.budget_alert === 'warning' ? 'bg-amber-50 border border-amber-200 text-amber-800' : 'bg-blue-50 border border-blue-200 text-blue-800'}`}>
              <AlertTriangle className="w-5 h-5" />
              <span className="font-medium">{finance.budget_alert === 'critical' ? 'Budget depasse !' : finance.budget_alert === 'warning' ? 'Budget presque epuise (>90%)' : 'Budget utilise a plus de 75%'}</span>
            </div>
          )}

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Depenses</p><p className="text-xl font-bold text-red-600">{fmt(finance.total_expenses)}</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Cout temps</p><p className="text-xl font-bold text-orange-600">{fmt(finance.time_cost)}</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Facture</p><p className="text-xl font-bold text-emerald-600">{fmt(finance.total_invoiced)}</p>
              <p className="text-xs text-slate-400">Paye: {fmt(finance.total_paid)}</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Profit (marge {finance.margin_percent}%)</p>
              <p className={`text-xl font-bold ${finance.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>{fmt(finance.profit)}</p>
            </CardContent></Card>
          </div>

          {/* Remaining */}
          <div className="grid md:grid-cols-3 gap-4">
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Reste a depenser</p><p className="text-lg font-bold">{fmt(finance.remaining_to_spend)}</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Reste a facturer</p><p className="text-lg font-bold text-amber-600">{fmt(finance.remaining_to_invoice)}</p>
            </CardContent></Card>
            <Card className="border-slate-200"><CardContent className="p-4">
              <p className="text-xs text-slate-500">En attente paiement</p><p className="text-lg font-bold text-blue-600">{fmt(finance.total_pending)}</p>
            </CardContent></Card>
          </div>

          {/* Charts */}
          <div className="grid md:grid-cols-2 gap-6">
            {/* Expense by category */}
            {finance.expense_by_category.length > 0 && (
              <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Depenses par categorie</CardTitle></CardHeader>
                <CardContent><div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart><Pie data={finance.expense_by_category} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={80} label={({name, value}) => `${value.toFixed(0)} CHF`}>
                      {finance.expense_by_category.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                    </Pie><Tooltip formatter={(v) => fmt(v)} /></PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap gap-2 mt-2">{finance.expense_by_category.map((c, i) => (
                  <span key={i} className="flex items-center gap-1 text-xs"><span className="w-2 h-2 rounded-full" style={{backgroundColor: PIE_COLORS[i % PIE_COLORS.length]}} />{c.category}: {fmt(c.amount)}</span>
                ))}</div>
              </CardContent></Card>
            )}

            {/* Monthly chart */}
            {finance.monthly_chart.length > 0 && (
              <Card className="border-slate-200"><CardHeader className="pb-2"><CardTitle className="text-sm">Evolution mensuelle</CardTitle></CardHeader>
                <CardContent><div className="h-[200px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={finance.monthly_chart}><XAxis dataKey="month" tick={{fontSize: 11}} /><YAxis tick={{fontSize: 11}} />
                      <Tooltip formatter={(v) => fmt(v)} /><Bar dataKey="expenses" fill="#ef4444" name="Depenses" /><Bar dataKey="time_cost" fill="#f59e0b" name="Temps" /><Bar dataKey="invoiced" fill="#10b981" name="Facture" />
                    </BarChart>
                  </ResponsiveContainer>
                </div></CardContent></Card>
            )}
          </div>
        </div>
      )}

      {/* ===== REPORTS TAB ===== */}
      {tab === 'reports' && <ProjectReportsTab projectId={projectId} projectName={project.name} />}

      {/* ===== ACTIVITY TAB ===== */}
      {tab === 'activity' && (
        <Card className="border-slate-200"><CardContent className="p-4">
          {activities.length > 0 ? (
            <div className="space-y-3">
              {activities.map(a => (
                <div key={a.id} className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-0">
                  <div className="w-8 h-8 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Activity className="w-3.5 h-3.5 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm"><span className="font-medium text-slate-900">{a.user_name}</span> <span className="text-slate-600">{a.detail}</span></p>
                    <p className="text-xs text-slate-400">{new Date(a.created_at).toLocaleString('fr-CH')}</p>
                  </div>
                </div>
              ))}
            </div>
          ) : <p className="text-center text-slate-400 py-8">Aucune activite</p>}
        </CardContent></Card>
      )}

      {/* Task Dialog */}
      <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
        <DialogContent className="sm:max-w-[450px]"><DialogHeader><DialogTitle>{editingTask ? 'Modifier' : 'Nouvelle tache'}</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTask} className="space-y-3">
            <div><Label>Titre *</Label><Input value={taskForm.title} onChange={(e) => setTaskForm({ ...taskForm, title: e.target.value })} required data-testid="task-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Priorite</Label><Select value={taskForm.priority} onValueChange={(v) => setTaskForm({ ...taskForm, priority: v })}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="low">Basse</SelectItem><SelectItem value="medium">Moyenne</SelectItem><SelectItem value="high">Haute</SelectItem></SelectContent></Select></div>
              <div><Label>Heures estimees</Label><Input type="number" step="0.5" min="0" value={taskForm.estimated_hours || ''} onChange={(e) => setTaskForm({ ...taskForm, estimated_hours: parseFloat(e.target.value) || 0 })} placeholder="0" data-testid="task-estimated-hours" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Date debut</Label><Input type="date" value={taskForm.start_date} onChange={(e) => setTaskForm({ ...taskForm, start_date: e.target.value })} data-testid="task-start-date" /></div>
              <div><Label>Deadline</Label><Input type="date" value={taskForm.deadline} onChange={(e) => setTaskForm({ ...taskForm, deadline: e.target.value })} data-testid="task-deadline" /></div>
            </div>
            <div><Label>Assignee</Label><Input value={taskForm.assignee} onChange={(e) => setTaskForm({ ...taskForm, assignee: e.target.value })} placeholder="Nom..." /></div>
            {tasks && tasks.length > 0 && editingTask && (
              <div><Label>Dependances</Label>
                <div className="max-h-24 overflow-y-auto border rounded-md p-2 space-y-1">
                  {tasks.filter(t => t.id !== editingTask?.id).map(t => (
                    <label key={t.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 p-0.5 rounded">
                      <input type="checkbox" checked={(taskForm.dependencies || []).includes(t.id)} onChange={() => {
                        const deps = taskForm.dependencies || [];
                        setTaskForm({ ...taskForm, dependencies: deps.includes(t.id) ? deps.filter(d => d !== t.id) : [...deps, t.id] });
                      }} className="rounded" />
                      <span className="truncate">{t.title}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setTaskDialogOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-task-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}{editingTask ? 'Modifier' : 'Creer'}</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Time Dialog */}
      <Dialog open={timeDialogOpen} onOpenChange={setTimeDialogOpen}>
        <DialogContent className="sm:max-w-[400px]"><DialogHeader><DialogTitle>Enregistrer du temps</DialogTitle></DialogHeader>
          <form onSubmit={handleCreateTime} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Heures *</Label><Input type="number" step="0.25" min="0.25" value={timeForm.hours} onChange={(e) => setTimeForm({ ...timeForm, hours: parseFloat(e.target.value) || 0 })} onFocus={(e) => e.target.select()} data-testid="time-hours" /></div>
              <div><Label>Date</Label><Input type="date" value={timeForm.date} onChange={(e) => setTimeForm({ ...timeForm, date: e.target.value })} /></div>
            </div>
            <div><Label>Description</Label><Input value={timeForm.description} onChange={(e) => setTimeForm({ ...timeForm, description: e.target.value })} placeholder="Qu'avez-vous fait ?" data-testid="time-description" /></div>
            {project.hourly_rate > 0 && <p className="text-sm text-slate-500">Cout: <strong>{fmt(timeForm.hours * project.hourly_rate)}</strong></p>}
            <DialogFooter><Button type="button" variant="outline" onClick={() => setTimeDialogOpen(false)}>Annuler</Button><Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="save-time-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4 mr-1" />}Enregistrer</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectDetailPage;
